import {
  WorkOrderFlowStepType,
  WorkOrderTriggerSource,
} from "@redotech/redo-model/work-order-flow/work-order-flow";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

const workStepSchema = z.object({
  id: z.string(),
  type: z.literal(WorkOrderFlowStepType.WORK_STEP),
  title: z.string().optional(),
  nextId: z.string().nullish(),
  instructions: z.string().optional(),
  displayAttributes: z
    .array(z.object({ label: z.string(), attributeKey: z.string() }))
    .optional(),
  inputs: z
    .array(z.object({ kind: z.string(), id: z.string(), quantity: z.number() }))
    .optional(),
  outputs: z
    .array(z.object({ kind: z.string(), id: z.string(), quantity: z.number() }))
    .optional(),
  stationId: z.string().nullable().optional(),
  printBarcodeOnComplete: z.boolean().optional(),
});

const conditionStepSchema = z.object({
  id: z.string(),
  type: z.literal(WorkOrderFlowStepType.CONDITION),
  conditionType: z.string().optional(),
  matchType: z.string().optional(),
  matchValues: z.array(z.string()).optional(),
  matchValue: z.unknown().optional(),
  matchDate: z.string().nullish(),
  matchKey: z.string().optional(),
  where: z.array(z.unknown()).optional(),
  nextTrueId: z.string().optional(),
  nextFalseId: z.string().optional(),
});

const triggerLeafNodeSchema = z.object({
  nodeType: z.literal("leaf"),
  conditionType: z.string(),
  matchType: z.string().optional(),
  matchValues: z.array(z.string()).optional(),
  matchValue: z.unknown().optional(),
  matchKey: z.string().optional(),
  where: z.array(z.unknown()).optional(),
});

export type TriggerNode =
  | z.infer<typeof triggerLeafNodeSchema>
  | TriggerGroupNode;
export interface TriggerGroupNode {
  nodeType: "group";
  conjunctionMode: string;
  children: TriggerNode[];
}

const triggerGroupNodeSchema: z.ZodType<TriggerGroupNode> = z.lazy(() =>
  z.object({
    nodeType: z.literal("group"),
    conjunctionMode: z.string(),
    children: z.array(triggerNodeSchema),
  }),
);

const triggerNodeSchema: z.ZodType<TriggerNode> = z.lazy(() =>
  z.union([triggerLeafNodeSchema, triggerGroupNodeSchema]),
);

const triggerConditionsSchema = z.object({
  conjunctionMode: z.string(),
  children: z.array(triggerNodeSchema),
});

export const updateWorkOrderFlowSteps = {
  name: "updateWorkOrderFlowSteps" as const,
  input: z.object({
    flowId: z.string(),
    triggerSource: createEnum(WorkOrderTriggerSource).optional(),
    triggerBomIds: z.array(z.string()).optional(),
    triggerBomTags: z.array(z.string()).optional(),
    triggerConditions: triggerConditionsSchema.optional(),
    batchMinSize: z.number().int().positive().optional(),
    batchMaxSize: z.number().int().positive().optional(),
    steps: z.array(z.union([workStepSchema, conditionStepSchema])),
    pickStationId: z.string().nullable().optional(),
    placeStationId: z.string().nullable().optional(),
    skipPlacing: z.boolean().optional(),
    autoPlaceStorageLocationId: z.string().nullable().optional(),
  }),
  output: z.object({ success: z.boolean() }),
};
